import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { useToast } from "@/shared/hooks/use-toast";
import { IntegrationBase, IntegrationDependencyError } from "../types/integration";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { useViewScope } from "@/shared/hooks/use-view-scope";
import { analyticsService } from "@/shared/services/analytics";

interface IntegrationsDeleteProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  integration: IntegrationBase | null;
  /**
   * Organização dona da integração, quando NÃO é a ativa da sessão (AB#85605).
   *
   * É o caso dos provedores da plataforma: o superadmin os administra em escopo de
   * plataforma, sem organização ativa.
   */
  organizationId?: string;
}

export const IntegrationsDelete = ({
  isOpen,
  onOpenChange,
  integration,
  organizationId
}: IntegrationsDeleteProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();
  const { organizationId: viewOrganizationId } = useViewScope();
  // Em Visão da organização não há workspace selecionado, mas a integração
  // (global) ainda pode ser excluída. Usamos a org do contexto de visão.
  const effectiveOrgId = organizationId || currentWorkspace?.organization_id || viewOrganizationId || null;

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!integration || !effectiveOrgId || isDeleting) return;

    setIsDeleting(true);

    try {
      // `effectiveOrgId`, e nao a prop crua: a guarda acima e a invalidacao de cache abaixo
      // usam o escopo resolvido, e mandar outro na chamada faria a exclusao sair por um
      // escopo diferente do que a tela validou. Quando a prop nao vem, o escopo resolvido e
      // exatamente o que a sessao usaria.
      await IntegrationBaseService.delete(integration.id, effectiveOrgId);

      // Invalidar cache para recarregar lista
      await queryClient.invalidateQueries({
        queryKey: ["integrations-base", effectiveOrgId]
      });
      
      toast({
        title: t("common.success"),
        description: `${integration.label} ${t("integrations.messages.deleteSuccess")}`,
      });

      // Track successful removal
      analyticsService.trackEvent('integration_removed', {
        integration_name: integration.name || integration.label
      });
      
      // Fechar dialog
      onOpenChange(false);
      
    } catch (error) {
      console.error('Erro ao remover integração:', error);
      
      // Tratar erro específico de dependências
      if (error instanceof IntegrationDependencyError) {
        const { dependencies } = error;
        
        // Criar mensagem detalhada sobre as dependências
        const dependencyMessages = [];
        if (dependencies.organizations > 0) {
          dependencyMessages.push(`${dependencies.organizations} organização${dependencies.organizations > 1 ? 'ões' : ''}`);
        }
        if (dependencies.agentIntegrations > 0) {
          dependencyMessages.push(`${dependencies.agentIntegrations} agente${dependencies.agentIntegrations > 1 ? 's' : ''}`);
        }
        
        const dependencyText = dependencyMessages.join(' e ');
        
        toast({
          title: t("integrations.messages.cannotDelete"),
          description: t("integrations.messages.hasDependencies", { dependencies: dependencyText }),
          variant: "destructive"
        });
      } else {
        // Erro genérico
        toast({
          title: t("common.error"),
          description: t("integrations.messages.deleteError"),
          variant: "destructive"
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            {t('integrations.delete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              <Trans
                i18nKey="integrations.delete.confirmMessage"
                values={{ name: integration?.label }}
                components={{ strong: <strong /> }}
              />
              <br />
              <br />
              {t('integrations.delete.warning')}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('common.actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? t('integrations.delete.removing') : t('integrations.delete.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
